import React from "react";
import { CheckIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import { ProductStoreOrderStatus } from "../../api/fetchProductStoreOrders/types";
import { UpdatableOrderStatus } from "../../api/updateProductStoreOrderStatus/types";
import { ORDER_STATUS_STYLES } from "../../utils/orderStatusStyles";

const PHYSICAL_ORDER_STEPS: ProductStoreOrderStatus[] = [
  "paid",
  "shipped",
  "completed",
];
// Digital-only orders have nothing to ship, so they skip the "shipped" step.
const DIGITAL_ORDER_STEPS: ProductStoreOrderStatus[] = ["paid", "completed"];

interface OrderStatusProgressProps {
  status: ProductStoreOrderStatus;
  isDigitalOnly?: boolean;
  onAdvance?: (target: UpdatableOrderStatus) => void;
}

const OrderStatusProgress: React.FC<OrderStatusProgressProps> = ({
  status,
  isDigitalOnly = false,
  onAdvance,
}) => {
  const steps = isDigitalOnly ? DIGITAL_ORDER_STEPS : PHYSICAL_ORDER_STEPS;
  const currentIdx = steps.indexOf(status);

  return (
    <div className="flex items-center gap-1.5">
      {steps.map((step, idx) => {
        const style = ORDER_STATUS_STYLES[step];
        const isDone = idx <= currentIdx;
        const isNext = idx === currentIdx + 1;
        const isClickable =
          isNext && step !== "paid" && onAdvance !== undefined;

        const baseClasses =
          "inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap transition-colors";

        const stateClasses = isDone
          ? "text-white"
          : isClickable
            ? "border border-dashed cursor-pointer hover:text-white"
            : "border border-dashed border-gray-300 text-gray-400 dark:text-gray-500";

        const inlineStyle: React.CSSProperties = isDone
          ? { backgroundColor: style.backgroundColor }
          : isClickable
            ? {
                borderColor: style.backgroundColor,
                color: style.backgroundColor,
              }
            : {};

        return (
          <React.Fragment key={step}>
            {idx > 0 && (
              <ChevronRightIcon
                aria-hidden="true"
                className="h-4 w-4 text-gray-400"
              />
            )}
            <button
              aria-label={
                isClickable
                  ? `Mark as ${style.label.toLowerCase()}`
                  : `${style.label} (${isDone ? "current or past" : "locked"})`
              }
              className={`${baseClasses} ${stateClasses}`}
              disabled={!isClickable}
              onClick={
                isClickable
                  ? (e) => {
                      // Row is expandable; don't toggle it when advancing status.
                      e.stopPropagation();
                      onAdvance?.(step as UpdatableOrderStatus);
                    }
                  : undefined
              }
              onMouseEnter={(e) => {
                if (isClickable) {
                  e.currentTarget.style.backgroundColor = style.backgroundColor;
                  e.currentTarget.style.color = "#fff";
                }
              }}
              onMouseLeave={(e) => {
                if (isClickable) {
                  e.currentTarget.style.backgroundColor = "";
                  e.currentTarget.style.color = style.backgroundColor;
                }
              }}
              style={inlineStyle}
              type="button"
            >
              {isDone && (
                <CheckIcon aria-hidden="true" className="h-3.5 w-3.5" />
              )}
              {style.label}
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default OrderStatusProgress;
